import { useState, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { uploadIdentificationImages } from '../services/uploadService'
import { submitIdentification } from '../services/identificationService'
import './IdentifyPage.css'
import imageCompression from 'browser-image-compression'



const MAX_FILE_SIZE = 10 * 1024 * 1024 // 10MB
const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp']

function IdentifyPage() {
    const [images, setImages] = useState([])

    const [isUploading, setIsUploading] = useState(false)
    const [isProcessingLocally, setIsProcessingLocally] = useState(false)
    const [uploadProgress, setUploadProgress] = useState({})
    const [error, setError] = useState(null)
    const [isDragging, setIsDragging] = useState(false)
    const fileInputRef = useRef(null)
    const navigate = useNavigate()

    const validateFile = (file) => {
        if (!ACCEPTED_TYPES.includes(file.type)) {
            return 'Unsupported file type. Please use JPG, PNG, or WebP.'
        }
        if (file.size > MAX_FILE_SIZE) {
            return 'File is too large. Maximum size is 10MB.'
        }
        return null
    }

    const processImage = async (file) => {
        const options = {
            maxSizeMB: 1, // Compress to 1MB
            maxWidthOrHeight: 2048,
            useWebWorker: true,
            fileType: 'image/jpeg',
            initialQuality: 0.8
            // browser-image-compression handles EXIF orientation automatically by default
        }

        try {
            const compressedFile = await imageCompression(file, options)

            // Create preview URL
            const previewUrl = await imageCompression.getDataUrlFromFile(compressedFile)

            return {
                id: crypto.randomUUID(),
                file: compressedFile, // Upload compressed file
                fileName: file.name,
                preview: previewUrl,
                name: file.name,
                originalSize: file.size,
                finalSize: compressedFile.size,
                resized: true
            }
        } catch (error) {
            console.error('Image compression failed:', error)
            throw new Error(`Failed to process image: ${file.name}`)
        }
    }

    const handleFiles = async (files) => {
        setError(null)
        setIsProcessingLocally(true)
        console.log('Processing files:', files.length)
        const newImages = []

        try {
            for (const file of files) {
                const validationError = validateFile(file)
                if (validationError) {
                    setError(validationError)
                    continue
                }

                const processedImage = await processImage(file)
                newImages.push(processedImage)
            }

            setImages(prev => [...prev, ...newImages])
        } catch (err) {
            console.error('Local image processing failed:', err)
            setError(`Failed to process some images: ${err.message}`)
        } finally {
            setIsProcessingLocally(false)
        }
    }

    const handleDrop = (e) => {
        e.preventDefault()
        setIsDragging(false)
        handleFiles(Array.from(e.dataTransfer.files))
    }

    const handleDragOver = (e) => {
        e.preventDefault()
        setIsDragging(true)
    }

    const handleDragLeave = () => {
        setIsDragging(false)
    }

    const handleFileInput = (e) => {
        handleFiles(Array.from(e.target.files))
        e.target.value = '' // Reset to allow same file selection
    }

    const removeImage = (index) => {
        setImages(prev => prev.filter((_, i) => i !== index))
    }

    const handleSubmit = async (e) => {
        e.preventDefault()
        setError(null)

        if (images.length === 0) {
            setError('Please upload at least one image.')
            return
        }



        setIsUploading(true)
        const sessionId = crypto.randomUUID()
        console.log('Initiating submission, Session ID:', sessionId)

        try {
            // 1. Upload images to Storage
            console.log('Uploading images to Storage...')
            const uploadedImages = await uploadIdentificationImages(
                images,
                sessionId,
                (imageId, progress) => {
                    setUploadProgress(prev => ({ ...prev, [imageId]: progress }))
                }
            )
            console.log('Uploads complete:', uploadedImages.length)

            const id = await submitIdentification({
                sessionId,
                category: 'porcelain',
                images: uploadedImages,
                mainImageUrl: uploadedImages[0].url,
                status: 'pending'
            })
            console.log('Identification submitted successfully, ID:', id)

            // 4. Navigate to results
            navigate(`/results/${id}`)
        } catch (err) {
            console.error('CRITICAL: Submission failed with error:', err)
            const errorMsg = err.code ? `[${err.code}] ${err.message}` : err.message
            setError(`Failed to submit: ${errorMsg}. Please ensure you are logged in and have a working connection.`)
        } finally {
            setIsUploading(false)
        }
    }

    // Mobile-specific camera handling
    const cameraInputRef = useRef(null)

    return (
        <div className="identify-page">
            <div className="container">
                <div className="page-header">
                    <h1>Identify Your Item</h1>
                    <p>Upload images of your vintage tableware or decorative item for AI-powered identification and appraisal.</p>
                </div>

                <form onSubmit={handleSubmit} className="identify-form">
                    {error && (
                        <div className="form-error">
                            <span className="error-icon">⚠</span>
                            {error}
                        </div>
                    )}



                    {/* Image Upload */}
                    <div className="form-section">
                        <label className="section-label">
                            Images
                            <span className="label-hint">Upload main view + optional detail shots</span>
                        </label>

                        {/* Mobile Capture Options (Visible only on mobile/touch) */}
                        <div className="mobile-capture-options">
                            <button
                                type="button"
                                className="capture-btn camera"
                                onClick={() => cameraInputRef.current?.click()}
                                disabled={isProcessingLocally}
                            >
                                <span className="btn-icon">📸</span>
                                Take Photo
                            </button>
                            <button
                                type="button"
                                className="capture-btn gallery"
                                onClick={() => fileInputRef.current?.click()}
                                disabled={isProcessingLocally}
                            >
                                <span className="btn-icon">📁</span>
                                Choose from Gallery
                            </button>
                        </div>

                        {/* Desktop Drop Zone (Hidden on mobile via CSS) */}
                        <div
                            className={`drop-zone ${isDragging ? 'dragging' : ''} ${isProcessingLocally ? 'processing' : ''}`}
                            onDrop={handleDrop}
                            onDragOver={handleDragOver}
                            onDragLeave={handleDragLeave}
                            onClick={() => !isProcessingLocally && fileInputRef.current?.click()}
                        >
                            <div className="drop-zone-content">
                                {isProcessingLocally ? (
                                    <>
                                        <div className="loading-spinner" />
                                        <p className="drop-text">Preparing your images...</p>
                                    </>
                                ) : (
                                    <>
                                        <span className="drop-icon">📷</span>
                                        <p className="drop-text">
                                            Drag & Drop images of your item here,
                                            <br />or click to select files
                                        </p>
                                    </>
                                )}
                                <p className="drop-hint">
                                    Supported formats: JPG, PNG, WebP. Max file size: 10MB
                                </p>
                            </div>

                            {/* Standard File Input (Gallery/Desktop) */}
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept={ACCEPTED_TYPES.join(',')}
                                multiple
                                onChange={handleFileInput}
                                className="file-input"
                                disabled={isProcessingLocally}
                            />

                            {/* Camera-specific Input (Hidden, triggered by button) */}
                            <input
                                ref={cameraInputRef}
                                type="file"
                                accept="image/*"
                                capture="environment"
                                onChange={handleFileInput}
                                className="file-input camera-input"
                                disabled={isProcessingLocally}
                                style={{ display: 'none' }}
                            />
                        </div>
                    </div>

                    {/* Image Previews */}
                    {images.length > 0 && (
                        <div className="form-section">
                            <label className="section-label">
                                Uploaded Images ({images.length})
                                <span className="label-hint">AI auto-detects which image is the hallmark</span>
                            </label>
                            <div className="image-grid">
                                {images.map((image, index) => (
                                    <div key={image.id} className="image-card">
                                        <img src={image.preview} alt={image.name} className="image-preview" />
                                        {image.resized && (
                                            <div className="resized-badge">Resized</div>
                                        )}
                                        {isUploading && uploadProgress[image.id] !== undefined && (
                                            <div className="upload-progress-overlay">
                                                <div
                                                    className="progress-bar-fill"
                                                    style={{ width: `${uploadProgress[image.id]}%` }}
                                                />
                                                <span className="progress-text">{Math.round(uploadProgress[image.id])}%</span>
                                            </div>
                                        )}
                                        <div className="image-actions">
                                            <button
                                                type="button"
                                                className="image-btn remove"
                                                onClick={() => removeImage(index)}
                                                aria-label="Remove image"
                                            >
                                                ✕
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Submit Button */}
                    <div className="form-actions">
                        <button
                            type="submit"
                            className="btn btn-primary submit-btn"
                            disabled={isUploading || isProcessingLocally || images.length === 0}
                        >
                            {isUploading ? (
                                <>
                                    <span className="loading-spinner small" />
                                    Submitting Identification...
                                </>
                            ) : isProcessingLocally ? (
                                'Processing Images...'
                            ) : (
                                'Identify Item'
                            )}
                        </button>
                        <p className="submit-note">
                            By clicking "Identify Item", you agree to our Terms of Service and Privacy Policy.
                        </p>
                    </div>
                </form>
            </div>
        </div>
    )
}

export default IdentifyPage
